import { Table } from 'antd';
import { useEffect, useRef, useState } from 'react';
import arrow from '../../assets/arrow-down.svg';
import noData from '../../assets/noData.svg';
import NoUser from '../../assets/NoUser.svg';
import type { ColumnsType } from 'antd/es/table';
import { Pagination } from './Pagination';
import { useAppSelector } from '../../hooks';
import {
  TableParamsReports,
  TableItemReportAccountType,
  TotalsData,
  Totals,
} from '../../api/types/reports';
import './index.scss';
import { MAIN_ACCOUNT, PARTNER } from '../../helpers/variables';

interface PartnersTableProps {
  data: TableItemReportAccountType[];
  setTableParams: React.Dispatch<React.SetStateAction<TableParamsReports>>;
  metaData: any;
  tableParams: TableParamsReports;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  isCard?: boolean;
  dataTotals?: Totals;
}

export const ReportsLenders = ({
  data,
  setTableParams,
  metaData,
  tableParams,
  setTrigger,
  dataTotals,
}: PartnersTableProps) => {
  const ref = useRef(null);
  const user = useAppSelector((state) => state.profile);

  const [tableData, setTableData] = useState<TableItemReportAccountType[]>([]);

  useEffect(() => {
    if (!data) return;
    const newTable: any = [...data];
    newTable.push(dataTotals);
    setTableData(newTable);
  }, [data, dataTotals]);

  const sorting = (name: string) => {
    setTableParams((prev: TableParamsReports) => ({
      ...prev,
      order_by: name,
      order:
        prev.order_by === name
          ? prev.order === 'desc'
            ? 'asc'
            : 'desc'
          : 'asc',
    }));
  };

  const columns: ColumnsType<TableItemReportAccountType> = [
    {
      title: (
        <div className="header-item" onClick={() => sorting('bank')}>
          Lender
          <img
            src={arrow}
            className={`header-image ${
              tableParams.order_by === 'bank' && tableParams.order === 'asc'
                ? 'active'
                : ''
            }`}
          />
        </div>
      ),
      dataIndex: 'bank',
      key: 'bank',
      render: (text, _, index) => (
        <>
          {tableData.length - 1 === index ? (
            <div className="table-item-footer">
              <div className="table-text">
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          ) : (
            <div className="table-item">
              <div className="table-text">{text}</div>
            </div>
          )}
        </>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('approved')}>
          Approved
          <img
            src={arrow}
            className={`header-image ${
              tableParams.order_by === 'approved' && tableParams.order === 'asc'
                ? 'active'
                : ''
            }`}
          />
        </div>
      ),
      dataIndex: 'approved',
      key: 'approved',
      render: (text, _, index) => (
        <>
          {tableData.length - 1 === index ? (
            <div className="table-item-footer">
              <div className="table-text">
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          ) : (
            <div className="table-item">
              <div className="table-text">
                {' '}
                {text
                  ? text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  : '-'}
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('rate')}>
          Average Interest Rate
          <img
            src={arrow}
            className={`header-image ${
              tableParams.order_by === 'rate' && tableParams.order === 'asc'
                ? 'active'
                : ''
            }`}
          />
        </div>
      ),
      dataIndex: 'rate',
      key: 'rate',
      render: (text, record, index) => (
        <>
          {tableData.length - 1 === index ? (
            <div className="table-item-footer">
              <div className="table-text">
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          ) : (
            <div className="table-item">
              <div className="table-text">
                {' '}
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}%
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('ala')}>
          ALA
          <img
            src={arrow}
            className={`header-image ${
              tableParams.order_by === 'ala' && tableParams.order === 'asc'
                ? 'active'
                : ''
            }`}
          />
        </div>
      ),
      dataIndex: 'ala',
      key: 'ala',
      render: (text, record, index) => (
        <>
          {tableData.length - 1 === index ? (
            <div className="table-item-footer">
              <div className="table-text">
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          ) : (
            <div className="table-item">
              <div className="table-text">
                {' '}
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('loan_amount')}>
          Funding
          <img
            src={arrow}
            className={`header-image ${
              tableParams.order_by === 'loan_amount' &&
              tableParams.order === 'asc'
                ? 'active'
                : ''
            }`}
          />
        </div>
      ),
      dataIndex: 'loan_amount',
      key: 'loan_amount',
      render: (text, record, index) => (
        <>
          {tableData.length - 1 === index ? (
            <div className="table-item-footer">
              <div className="table-text">
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          ) : (
            <div className="table-item">
              <div className="table-text">
                {' '}
                {text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('approved')}>
          Bank Fee
        </div>
      ),
      width: 100,
      dataIndex: 'bank_fee_amount',
      key: 'bank_fee_amount',
      render: (text, record, index) => (
        <div
          className={
            tableData.length - 1 === index ? 'table-item-footer' : 'table-item'
          }
        >
          <div className="table-text">
            {' '}
            ${text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('approved')}>
          Magwitch Fee
        </div>
      ),
      width: 100,
      dataIndex: 'processing_fee_amount',
      key: 'processing_fee_amount',
      render: (text, record, index) => (
        <div
          className={
            tableData.length - 1 === index ? 'table-item-footer' : 'table-item'
          }
        >
          <div className="table-text">
            {' '}
            ${text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('approved')}>
          Discount Fee
        </div>
      ),
      width: 100,
      dataIndex: 'discount_fee_amount',
      key: 'discount_fee_amount',
      render: (text, record, index) => (
        <div
          className={
            tableData.length - 1 === index ? 'table-item-footer' : 'table-item'
          }
        >
          <div className="table-text">
            ${text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('approved')}>
          Sent to Partner{' '}
        </div>
      ),
      width: 120,
      dataIndex: 'sent_to_partner',
      key: 'sent_to_partner',
      render: (text, record, index) => (
        <div
          className={
            tableData.length - 1 === index ? 'table-item-footer' : 'table-item'
          }
        >
          <div className="table-text">
            ${text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('approved')}>
          Part. Bonus
        </div>
      ),
      width: 120,
      dataIndex: 'participation_bonus_amount',
      key: 'participation_bonus_amount',
      render: (text, record, index) => (
        <div
          className={
            tableData.length - 1 === index ? 'table-item-footer' : 'table-item'
          }
        >
          <div className="table-text">
            ${text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="header-item" onClick={() => sorting('revenue')}>
          Revenue{' '}
          <img
            src={arrow}
            className={`header-image ${
              tableParams.order_by === 'revenue' && tableParams.order === 'asc'
                ? 'active'
                : ''
            }`}
            alt="arrow"
          />
        </div>
      ),
      width: 100,
      dataIndex: 'revenue',
      key: 'revenue',
      render: (text, record, index) => (
        <>
          {' '}
          {tableData.length - 1 === index ? (
            <div className="table-item-footer">
              <div className="table-text">
                ${text?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          ) : (
            <div className="table-item">
              <div className="table-text">
                $
                {Math.round(text)
                  ?.toString()
                  ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </div>
            </div>
          )}
        </>
      ),
    },
  ];

  if (user.role.key === PARTNER || user.role.key === MAIN_ACCOUNT) {
    columns.splice(-2);
  }

  const customNoDataText = {
    emptyText: (
      <>
        {!tableParams.date_from && !tableParams.search && (
          <div className="no-data-found">
            <img src={NoUser} />
            <div className="no-data-found__text">No reports yet</div>
          </div>
        )}
        {(tableParams.date_from || tableParams.search) && (
          <div className="no-data-found">
            <img src={noData} />
            <div className="no-data-found__text">No results found</div>
            <div className="no-data-found__subtext">
              Try adjusting your search to find what you are looking for
            </div>
          </div>
        )}
      </>
    ),
  };

  if (user.role.key === 'main_account') {
    const roleColumnIndex = columns.findIndex(
      (column) => column.key === 'main_account'
    );
    if (roleColumnIndex !== -1) {
      columns.splice(roleColumnIndex, 1);
    }
  }

  return (
    <>
      <div>
        <Table
          ref={ref}
          columns={columns}
          dataSource={tableData}
          pagination={false}
          locale={customNoDataText}
        />
      </div>

      {data?.length > 0 && (
        <Pagination
          setTableParams={setTableParams}
          metaData={metaData}
          tableParams={tableParams}
        />
      )}
    </>
  );
};
